/**
 * canvas-engine/renderer/elements/ImageGridElement.jsx
 *
 * OWN per-type element renderer for `imageGrid` elements.
 *
 * ZERO REUSE: imports NOTHING from template-builder/ or academy-ui/src/template/.
 *
 * Inner-content vs positioning CONTRACT: renders ONLY the inner grid of images,
 * filling its positioned box. The parent CanvasRenderer owns positioning. DOM
 * output is identical in builder and reader modes. (Req 4.1, 16.1)
 *
 * Lays out `content.images` (asset refs) in `layout.columns` / `layout.gap`.
 * Pure / presentational. Images resolved via the OWN `assetRef`.
 */

import React from 'react';
import { assetRef } from '../../model/assetRef';
import { EngineImage } from '../../model/assetUrlResolver';

function num(v, fallback) {
  return Number.isFinite(v) ? v : fallback;
}

/**
 * Render a grid of images.
 *
 * @param {object} props
 * @param {object} props.element normalized `imageGrid` Canvas_Element
 */
export default function ImageGridElement({ element }) {
  const content = (element && element.content) || {};
  const style = (element && element.style) || {};
  const layout = (element && element.layout) || {};

  const images = Array.isArray(content.images) ? content.images : [];
  const columns = Math.max(1, num(layout.columns, 3));
  const gap = num(layout.gap, 14);
  const imageRadius = num(style.imageRadius, 12);
  const objectFit = typeof style.objectFit === 'string' ? style.objectFit : 'cover';

  const rootStyle = {
    width: '100%',
    height: '100%',
    boxSizing: 'border-box',
    display: 'grid',
    gridTemplateColumns: `repeat(${columns}, 1fr)`,
    gap,
    overflow: 'hidden',
  };

  return (
    <div className="ce-image-grid" style={rootStyle}>
      {images.map((image, index) => {
        const asset = assetRef(image && typeof image === 'object' && 'asset' in image ? image.asset : image);
        const src = asset.displayLink || asset.storedLink || '';
        const alt =
          image && typeof image === 'object' && typeof image.alt === 'string' ? image.alt : `image ${index + 1}`;
        const key = `image-grid-${index}`;

        return (
          <div key={key} className="ce-image-grid__cell" style={{ minWidth: 0 }}>
            {src ? (
              <EngineImage
                src={src}
                alt={alt}
                style={{ width: '100%', height: '100%', objectFit, borderRadius: imageRadius, display: 'block' }}
              />
            ) : (
              <div
                style={{
                  width: '100%',
                  height: '100%',
                  minHeight: 100,
                  background: '#e2e8f0',
                  borderRadius: imageRadius,
                }}
                aria-label={alt}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}
